import { FeeCollectionRow } from '@klear/order-event-ui/timeline/fee-collection-row'
import { useEffect, useId, useState, type ComponentType, type ReactNode } from 'react'
import { Button } from '@klear/ui/components/button'
import { ChevronDown, ChevronRight, Plus, Truck } from 'lucide-react'
import { EventActions } from '@klear/order-event-ui/event-actions'
import type { Fee } from '@klear/fee'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import type { OrderEventType, OrderEventActionEnum } from '@klear/order-event/constants'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ACTION_ADD,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
} from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { EventTitle } from '@klear/order-event-ui/event-title'
import { PurchaseOrderView } from '@klear/order-event-ui/event-forms/purchase-order-form'
import { OperationalView } from '@klear/order-event-ui/event-forms/operational-form'
import { InvoiceView } from '@klear/order-event-ui/event-forms/invoice-form'
import { ProofOfAcceptanceView } from '@klear/order-event-ui/event-forms/proof-of-acceptance-form'
import { getEventFormHandler } from '@klear/order-event-ui/event-handlers/registry'
import constant from 'lodash/constant'
import isString from 'lodash/isString'
import type { OrderEventFeeCollection, OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId, timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'
import { domainListItemId } from '@klear/order-event/order-event-domain-list'
import { dateFormattedOrDash, domainListItemEffectiveDate, listItemDateEpochDay } from '@klear/order-event/list-display'
import type { KlearDate } from '@klear/date'
import type {
  OrderEventFilePreviewBreadcrumbItem,
  OrderEventFilePreviewOrderSummary
} from '@klear/order-event/file-preview-display'
import { orderEventFilePreviewContext } from '@klear/order-event/file-preview-display'
import { FileBadgeList } from '@klear/ui/components/file-badge-list'
import { useOrderEventDocumentsFor } from '@klear/order-event-ui/order-event-documents-context'
import {
  flattenTimelineEvents,
  isInvoiceEvent,
  isInvoiceOfferEvent,
  isOperationalEvent,
  isProofOfAcceptanceType,
  isPurchaseOrderEvent,
  buildTimelineSteps,
  orderEventTypeLabel,
  purchaseOrderDeliveryMarkers,
  showsStatusToTrader
} from '@klear/order-event-ui/event-timeline-utils'
import { domainListItemNumber } from '@klear/order-event/list-display'
import { orderEventShowsStatus } from '@klear/order-event/presentation'
import { useEventFormBootstrap } from '@klear/order-event-ui/event-shared/use-event-form-bootstrap'
import { Skeleton } from '@klear/ui/components/skeleton'
import type { TimelineActionContext } from '@klear/order-event-ui/event-actions-availability'

export type EventItemOperationalPaymentsComponent = ComponentType<{
  operationalEventId: number
  traderId: number
  orderId: number
}>

export type EventItemInvoiceOfferViewComponent = ComponentType<{
  offer: InvoiceOffer
  fees: Fee[]
  isAdminContext?: boolean
  creditClassification?: string
}>

interface EventItemProps {
  event: OrderEventTimelineItem
  depth?: number
  traderId: number
  orderId: number
  getEventUrl?: (item: OrderEventFilePreviewBreadcrumbItem) => string
  expanded?: boolean
  isEditing?: boolean
  id?: string
  isAdminContext?: boolean
  onView?: (event: OrderEventTimelineItem) => void
  onViewOffer?: (event: OrderEventTimelineItem) => void
  currentEventId?: number
  operationalPaymentsView?: EventItemOperationalPaymentsComponent
  onAddInvoiceOffer?: (parentEvent: OrderEventTimelineItem) => void
  onMarkComplete?: (event: OrderEventTimelineItem) => void
  onMarkFunded?: (event: OrderEventTimelineItem) => void
  onMarkFeesPaid?: (event: OrderEventTimelineItem) => void
  onAdvanceRecovery?: (event: OrderEventTimelineItem) => void
  onAddPayment?: (operational: Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_OPERATIONAL }>) => void
  invoiceOfferView?: EventItemInvoiceOfferViewComponent
  timelineActionContext?: TimelineActionContext
  timelineEventsLoading?: boolean
  filePreviewOrderSummary?: OrderEventFilePreviewOrderSummary
  bare?: boolean
  leadingIcon?: (event: OrderEventTimelineItem) => ReactNode
  focusedEventId?: number | null
}

function DeliveryRow({ label, date }: { label: string; date: KlearDate }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="w-4 shrink-0" aria-hidden />
      <Truck className="h-4 w-4 text-muted-foreground" />
      <div className="font-medium">{label}</div>
      <div className="text-sm text-muted-foreground">{dateFormattedOrDash(date)}</div>
    </div>
  )
}

function entryNotes(event: OrderEventTimelineItem): string {
  const notes = (event.model as { notes?: unknown }).notes
  return isString(notes) ? notes : ''
}

function EventItemBody({
  event,
  traderId,
  isAdminContext,
  invoiceOfferView: InvoiceOfferViewComponent
}: {
  event: OrderEventTimelineItem
  traderId: number
  isAdminContext: boolean
  invoiceOfferView?: EventItemInvoiceOfferViewComponent
}) {
  switch (event.type) {
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return <PurchaseOrderView purchaseOrder={event.model} />
    case PERMISSION_ENTITY_OPERATIONAL:
      return <OperationalView traderId={traderId} operational={event.model} />
    case PERMISSION_ENTITY_INVOICE:
      return <InvoiceView invoice={event.model} />
    case PERMISSION_ENTITY_INVOICE_OFFER:
      return InvoiceOfferViewComponent ? (
        <InvoiceOfferViewComponent offer={event.model} fees={event.fees} isAdminContext={isAdminContext} />
      ) : null
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return <ProofOfAcceptanceView proofOfAcceptance={event.model} />
    default:
      return null
  }
}

function FeeCollectionBlock({ fc }: { fc: OrderEventFeeCollection }) {
  return (
    <div>
      <FeeCollectionRow fc={fc} />
    </div>
  )
}

export interface EventFormWrapperProps {
  eventType: OrderEventType
  traderId: number
  orderId: number
  editEventId?: number
  parentEventId?: number
  onSuccess?: () => void
  onCancel?: () => void
  isAdminContext?: boolean
}

export function EventFormWrapper({
  eventType,
  traderId,
  orderId,
  editEventId,
  parentEventId,
  onSuccess,
  onCancel,
  isAdminContext = true
}: EventFormWrapperProps) {
  const { bootstrap, isLoading } = useEventFormBootstrap({
    eventType,
    editEventId,
    parentEventId,
    traderId
  })
  const Handler = getEventFormHandler(eventType)

  if (!Handler) {
    return null
  }

  if (isLoading || bootstrap == null) {
    return <Skeleton className="h-24 w-full" />
  }

  return (
    <Handler
      bootstrap={bootstrap}
      orderId={orderId}
      traderId={traderId}
      isAdminContext={isAdminContext}
      onSuccess={() => onSuccess?.()}
      onCancel={onCancel}
    />
  )
}

export function EventItem({
  id,
  event,
  depth = 0,
  traderId,
  orderId,
  getEventUrl = constant(''),
  expanded = true,
  isEditing = false,
  isAdminContext = true,
  onView,
  onViewOffer,
  currentEventId,
  operationalPaymentsView: OperationalPaymentsViewComponent,
  onAddInvoiceOffer,
  onMarkComplete,
  onMarkFunded,
  onMarkFeesPaid,
  onAdvanceRecovery,
  onAddPayment,
  invoiceOfferView: InvoiceOfferViewComponent,
  timelineActionContext,
  timelineEventsLoading,
  filePreviewOrderSummary,
  bare = false,
  leadingIcon,
  focusedEventId
}: EventItemProps) {
  const { user } = useAuth()
  const entryId = timelineItemId(event)
  const parentId = timelineItemParentId(event)
  const flatTimelineEvents =
    timelineActionContext != null ? flattenTimelineEvents(timelineActionContext.timeline) : [event]
  const filePreviewOrder = filePreviewOrderSummary ?? {
    idDisplay: event.envelope.orderIdDisplay,
    displayName: event.envelope.orderIdDisplay
  }
  const filePreviewContext = orderEventFilePreviewContext(event, flatTimelineEvents, traderId, filePreviewOrder)
  const childrenToShow = event.children
  const deliveries =
    event.type === PERMISSION_ENTITY_PURCHASE_ORDER && childrenToShow.length > 0
      ? purchaseOrderDeliveryMarkers(event.model)
      : []
  const childSteps = buildTimelineSteps(
    childrenToShow,
    (child) => listItemDateEpochDay(domainListItemEffectiveDate(child)),
    deliveries,
    'oldest'
  )
  const feeCollections = isInvoiceOfferEvent(event) ? event.feeCollections : []
  const hasFeeCollectionPayments = feeCollections.length > 0
  const showOperationalPayments = isOperationalEvent(event) && OperationalPaymentsViewComponent != null
  const hasExpandableContent = childrenToShow.length > 0 || hasFeeCollectionPayments || showOperationalPayments
  let expandLabel = 'Details'
  if (showOperationalPayments) expandLabel = 'Payments'
  else if (hasFeeCollectionPayments) expandLabel = 'Fee Collections'

  const [isExpanded, setIsExpanded] = useState(expanded)
  const panelId = useId()
  const containsFocused =
    focusedEventId != null && flattenTimelineEvents(childrenToShow).some((e) => timelineItemId(e) === focusedEventId)
  useEffect(() => {
    if (containsFocused) setIsExpanded(true)
  }, [containsFocused, focusedEventId])
  const reduceMotion = useReducedMotion()
  const motionDuration = reduceMotion ? 0 : 0.2
  const number = domainListItemNumber(event)
  const title = `${orderEventTypeLabel(event.type)} ${number ? `#${number}` : event.envelope.idDisplay}`
  const childSummary = childrenToShow.map((child) => ({
    id: timelineItemId(child),
    label: orderEventTypeLabel(child.type),
    status:
      orderEventShowsStatus(child.type) && (isAdminContext || showsStatusToTrader(child))
        ? child.envelope.statusLabel
        : null
  }))
  const [editingEventId, setEditingEventId] = useState<number | null>(isEditing ? entryId : null)
  const [addingEventType, setAddingEventType] = useState<OrderEventType | null>(null)

  function clearEditing() {
    setEditingEventId(null)
    setAddingEventType(null)
  }

  const handleSuccess = (action?: OrderEventActionEnum, addEventType?: OrderEventType) => {
    if (action === PERMISSION_ACTION_ADD && addEventType) {
      if (addEventType === PERMISSION_ENTITY_INVOICE_OFFER) {
        onAddInvoiceOffer?.(event)
        return
      }
      setAddingEventType(addEventType)
      setIsExpanded(true)
      return
    }
    clearEditing()
  }

  const notes = entryNotes(event).trim()
  const documents = useOrderEventDocumentsFor(domainListItemId(event))

  return (
    <div
      id={id}
      className={`${bare ? '' : 'py-1'} ${focusedEventId === entryId ? '-mx-2 rounded-lg bg-muted/60 px-2 py-2' : ''}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-2">
        {editingEventId !== entryId && (
          <div className="flex shrink-0 items-center gap-2">
            {hasExpandableContent ? (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} ${expandLabel.toLowerCase()} for ${title}`}
                className="-m-1 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-4 shrink-0" aria-hidden />
            )}
            {leadingIcon?.(event)}
            <EventTitle event={event} isAdminContext={isAdminContext} />
          </div>
        )}
        <div className="ml-auto flex min-w-0 items-center gap-2">
          {editingEventId !== entryId && documents.length > 0 ? (
            <FileBadgeList
              documents={documents}
              previewContext={filePreviewContext}
              getEventUrl={getEventUrl}
              className="flex-row flex-wrap justify-end gap-2"
            />
          ) : null}
          {isPurchaseOrderEvent(event) &&
            (user.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_ADD) ||
              user.hasPermission(PERMISSION_ENTITY_OPERATIONAL, PERMISSION_ACTION_ADD)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="xs" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Event
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {user.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_ADD) && (
                    <DropdownMenuItem onClick={() => setAddingEventType(PERMISSION_ENTITY_INVOICE)}>
                      Add Invoice
                    </DropdownMenuItem>
                  )}
                  {user.hasPermission(PERMISSION_ENTITY_OPERATIONAL, PERMISSION_ACTION_ADD) && (
                    <DropdownMenuItem onClick={() => setAddingEventType(PERMISSION_ENTITY_OPERATIONAL)}>
                      Add Operational
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          {isInvoiceEvent(event) &&
            user.hasPermission(PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE, PERMISSION_ACTION_ADD) &&
            !childrenToShow.some((c) => isProofOfAcceptanceType(c.type)) && (
              <Button
                size="xs"
                variant="outline"
                onClick={() => setAddingEventType(PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Proof of Acceptance
              </Button>
            )}
          {isOperationalEvent(event) &&
            onAddPayment != null &&
            user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_ADD) && (
              <Button size="xs" variant="outline" onClick={() => onAddPayment(event)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Payment
              </Button>
            )}
          <EventActions
            eventId={entryId}
            eventType={event.type}
            orderId={orderId}
            traderId={traderId}
            parentEventId={parentId}
            timelineActionContext={timelineActionContext}
            timelineEventsLoading={timelineEventsLoading}
            onView={onView ? () => onView(event) : undefined}
            isViewDisabled={currentEventId === entryId}
            onViewOffer={onViewOffer ? () => onViewOffer(event) : undefined}
            onEdit={isInvoiceOfferEvent(event) ? undefined : () => setEditingEventId(entryId)}
            canAddInvoiceOffer={onAddInvoiceOffer != null}
            onMarkComplete={
              onMarkComplete && isInvoiceOfferEvent(event) && event.model.canMarkCompleted
                ? () => onMarkComplete(event)
                : undefined
            }
            onMarkFunded={
              onMarkFunded && isInvoiceOfferEvent(event) && event.model.canMarkFunded
                ? () => onMarkFunded(event)
                : undefined
            }
            onMarkFeesPaid={
              onMarkFeesPaid && isInvoiceOfferEvent(event) && event.model.canMarkFeesPaid
                ? () => onMarkFeesPaid(event)
                : undefined
            }
            onAdvanceRecovery={
              onAdvanceRecovery && isInvoiceOfferEvent(event) && event.model.canReturnAdvance
                ? () => onAdvanceRecovery(event)
                : undefined
            }
            onSuccess={handleSuccess}
            isEditing={editingEventId === entryId}
            label={`Actions for ${title}`}
          />
        </div>
      </div>

      <div className={editingEventId === entryId ? 'space-y-2' : 'space-y-2 pl-6'}>
        <div className="text-sm">
          {editingEventId === entryId ? (
            <EventFormWrapper
              eventType={event.type}
              traderId={traderId}
              orderId={orderId}
              editEventId={entryId}
              parentEventId={parentId}
              onSuccess={() => clearEditing()}
              onCancel={() => clearEditing()}
              isAdminContext={isAdminContext}
            />
          ) : (
            <EventItemBody
              event={event}
              traderId={traderId}
              isAdminContext={isAdminContext}
              invoiceOfferView={InvoiceOfferViewComponent}
            />
          )}
        </div>

        {editingEventId !== entryId && notes ? (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{notes}</p>
        ) : null}

        {!isExpanded && childSummary.length > 0 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {childSummary.map((child) => (
              <span key={child.id}>
                {child.label}
                {child.status ? <span className="font-medium text-foreground"> · {child.status}</span> : null}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <AnimatePresence>
        {addingEventType && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: motionDuration }}
            className="mt-4 ml-4"
          >
            <EventFormWrapper
              eventType={addingEventType}
              traderId={traderId}
              orderId={orderId}
              parentEventId={entryId}
              onSuccess={() => clearEditing()}
              onCancel={() => clearEditing()}
              isAdminContext={isAdminContext}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {hasExpandableContent && (
        <div>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                id={panelId}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: motionDuration }}
                className="mt-4 ml-2 border-l pl-5 space-y-5 overflow-hidden"
              >
                {childSteps.map((step) => {
                  if (step.kind === 'delivery') {
                    return <DeliveryRow key={`delivery-${step.label}`} label={step.label} date={step.date} />
                  }
                  const child = step.event
                  const childId = timelineItemId(child)
                  return (
                    <EventItem
                      key={childId}
                      id={childId.toString()}
                      event={child}
                      depth={depth + 1}
                      traderId={traderId}
                      orderId={orderId}
                      getEventUrl={getEventUrl}
                      isAdminContext={isAdminContext}
                      onAddInvoiceOffer={onAddInvoiceOffer}
                      onView={onView}
                      onViewOffer={onViewOffer}
                      currentEventId={currentEventId}
                      onMarkComplete={onMarkComplete}
                      onMarkFunded={onMarkFunded}
                      onMarkFeesPaid={onMarkFeesPaid}
                      onAdvanceRecovery={onAdvanceRecovery}
                      onAddPayment={onAddPayment}
                      operationalPaymentsView={OperationalPaymentsViewComponent}
                      invoiceOfferView={InvoiceOfferViewComponent}
                      timelineActionContext={timelineActionContext}
                      timelineEventsLoading={timelineEventsLoading}
                      filePreviewOrderSummary={filePreviewOrderSummary}
                      leadingIcon={leadingIcon}
                      focusedEventId={focusedEventId}
                    />
                  )
                })}
                {showOperationalPayments && OperationalPaymentsViewComponent ? (
                  <div>
                    <OperationalPaymentsViewComponent
                      operationalEventId={entryId}
                      traderId={traderId}
                      orderId={orderId}
                    />
                  </div>
                ) : null}
                {hasFeeCollectionPayments &&
                  feeCollections.map((fc) => <FeeCollectionBlock key={fc.paymentId} fc={fc} />)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
